import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { signIn as exchangeToken, type SignInProvider } from '../legacy-api/chat';
import { legacyFairDomain } from '../legacy-api/client';

import { facebookAccessToken, googleAccessToken } from './providers';
import { clearSession, readSession, writeSession } from './session';

interface AuthContextValue {
  /** The legacy API's JWT; undefined when signed out. */
  token: string | undefined;
  /**
   * Opens the provider's sign-in window and exchanges its token. Must be called
   * from a click handler, with the provider's SDK already loaded.
   */
  signIn: (provider: SignInProvider, providerId: string) => Promise<void>;
  /** Also used when the API rejects the token, so an expired one is never kept. */
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Held above the routes so a sign-in carries over from one stand's chat to the next. */
export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [token, setToken] = useState<string | undefined>(readSession);

  const signIn = useCallback(
    async (provider: SignInProvider, providerId: string): Promise<void> => {
      // Opened first and synchronously: see providers.ts.
      const accessToken =
        provider === 'google' ? googleAccessToken(providerId) : facebookAccessToken(providerId);
      const jwt = await exchangeToken(provider, await accessToken, legacyFairDomain());

      writeSession(jwt);
      setToken(jwt);
    },
    [],
  );

  const signOut = useCallback((): void => {
    clearSession();
    setToken(undefined);
  }, []);

  const value = useMemo(() => ({ token, signIn, signOut }), [token, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }

  return context;
}
